import Text from '@/components/Text/Text'
import ComboBox from '@/components/forms/combo-box'
import { useMemo, useState } from 'react'
import { CheckIcon, XIcon } from 'lucide-react'
import { Button, Icon, LoadingAnimation } from '@/components'
import { match, P } from 'ts-pattern'
import UtilityPlanDropdown from 'features/utilities/UtilityPlanDropdown'
import { AlertMessage } from '@/app/components'
import ExternalUtilityConnectionLinks from '@/app/components/ExternalUtilityConnectionLinks/ExternalUtilityConnectionLinks'
import UtilityConnectLinkRow from '@/app/components/UtilityConnectLinkRow/utility-connect-link-row'
import {
  BillUploadCard,
  BillOnFileNotice,
} from '@/app/features/utility-account-fields'
import {
  EligibilityStatus,
  useUtilityPlanSelectionViewModel,
  UtilityPlanSelectionParams,
} from './useUtilityPlanSelectionViewModel'
import PlanRatePreview from './PlanRatePreview'

/**
 * Rotating status messages shown while a bill is processing — roughly tracking
 * the backend pipeline (upload → AI extraction → utility/plan matching → rates).
 */
const BILL_UPLOAD_LOADING_MESSAGES = [
  'Uploading your bill',
  'Reading your bill',
  'Reading your account details',
  'Finding your utility',
  'Matching your rate plan',
  'Pulling your rates',
  'Almost there',
]

/**
 * Inline eligibility status line for the utility or plan field — green when
 * eligible, red when not, nothing when there's no program to gate against.
 */
function EligibilityNotice({
  status,
  label,
}: {
  status: EligibilityStatus
  label: string
}) {
  return match(status)
    .with(EligibilityStatus.ELIGIBLE, () => (
      <Text variant="body3" className="text-themed-green-900">
        {label} is eligible
      </Text>
    ))
    .with(EligibilityStatus.INELIGIBLE, () => (
      <Text variant="body3" className="text-themed-error">
        {label} is not eligible
      </Text>
    ))
    .with(EligibilityStatus.NONE, () => null)
    .exhaustive()
}

export type UtilityPlanSelectionProps = UtilityPlanSelectionParams

/**
 * Shared "utility + plan selection" page used by both multi-program enrollment
 * and the profile utility-information flow. Lets the user upload a bill to
 * auto-fill their utility/plan, build a plan from the bill when no plan matched,
 * or select/add a utility and plan manually — and previews the selected plan's
 * time-of-use rates.
 */
export default function UtilityPlanSelection(props: UtilityPlanSelectionProps) {
  const viewModel = useUtilityPlanSelectionViewModel(props)
  const {
    form,
    options,
    initialUtility,
    utilityEligibilityStatus,
    onQueryChange,
    onNextClick,
    onSkip,
    planRequired,
    planOptions,
    fetchingPlans,
    setSelectedPlanId,
    selectedPlanId,
    planEligibilityStatus,
    isBuilding,
    isBusy,
    buildError,
    utilityProgram,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
    initialSelectedUtility,
    utilityRefId,
    onExternalUtilityConnectButtonClick,
    utilitySearchLoading,
    upload,
    planBuilt,
    addUtility,
    onAddPlan,
    seasonalRateSummary,
    isInitializing,
    hasSavedUtilityAndPlan,
  } = viewModel
  const { register, setValue, getValues } = form

  // A bill uploaded on an earlier visit is already on file — show the notice
  // instead of re-prompting, until the user opts to upload another.
  const [showUploaderOverride, setShowUploaderOverride] = useState(false)
  // The user already has a bill extraction and a saved utility/plan — they're
  // confirming/updating an existing setup rather than setting one up fresh.
  const isConfirming = upload.hasStoredExtraction && hasSavedUtilityAndPlan

  const billAlreadyOnFile =
    isConfirming &&
    upload.fileUploadStatus === 'idle' &&
    upload.resultToken === 0 &&
    !showUploaderOverride

  // A "buildable" upload (rate schedule but no matched plan) auto-kicks off the
  // build, so treat it as building from the moment the result lands — this also
  // covers the frame before the build effect fires, avoiding a flash of the
  // pre-build state.
  const isAutoBuilding =
    isBuilding || (upload.outcome === 'buildable' && !planBuilt && !buildError)

  const dropdownsDisabled =
    upload.fileUploadStatus === 'loading' || isAutoBuilding

  const utilityBoxIcon = useMemo(
    () =>
      match(utilityEligibilityStatus)
        .with(EligibilityStatus.ELIGIBLE, () => (
          <CheckIcon
            size={22}
            className="rounded-full p-1 bg-themed-green-900 text-themed-white"
          />
        ))
        .with(EligibilityStatus.INELIGIBLE, () => (
          <XIcon
            size={22}
            className="rounded-full p-1 bg-themed-error text-themed-white"
          />
        ))
        .with(EligibilityStatus.NONE, () => null)
        .exhaustive(),
    [utilityEligibilityStatus],
  )

  // "Your bill uploaded successfully" line, shown once inside the consolidated
  // result banner. We link a generic "Your bill" rather than the raw file name,
  // which can be long/odd. Shown only after an in-session upload.
  const billFileLine = upload.fileUploadName ? (
    <Text className="text-initial font-bold">
      {upload.billUrl ? (
        <a
          href={upload.billUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="!underline cursor-pointer"
        >
          Your bill
        </a>
      ) : (
        'Your bill'
      )}{' '}
      uploaded successfully.
    </Text>
  ) : null

  // Message shown after a bill has been processed. A "buildable" upload never
  // reaches here — it auto-builds (loader) and lands on the green planBuilt
  // message, or surfaces a build error below.
  const billResultAlert = planBuilt ? (
    <AlertMessage variant="success">
      {billFileLine}
      <Text
        variant="body2"
        className={billFileLine ? 'mt-2 text-initial' : 'text-initial'}
      >
        We created a custom rate plan from your bill and attached it to your
        account. Review it below.
      </Text>
    </AlertMessage>
  ) : (
    match({
      status: upload.fileUploadStatus,
      outcome: upload.outcome,
    })
      .with({ status: 'success', outcome: 'complete_match' }, () => (
        <AlertMessage variant="success">
          {billFileLine}
          <Text
            variant="body2"
            className={billFileLine ? 'mt-2 text-initial' : 'text-initial'}
          >
            We found your utility and rate plan from your bill. Confirm they are
            correct or update them below.
          </Text>
        </AlertMessage>
      ))
      .with({ status: 'success', outcome: 'manual' }, () =>
        utilityProgram ? (
          <AlertMessage variant="warning">
            We couldn’t find your rate plan from your bill. Please make sure all
            of its pages were uploaded or manually select your utility and plan
            below.
          </AlertMessage>
        ) : (
          <AlertMessage variant="warning">
            We couldn’t find your rate plan from your bill. Please make sure all
            of its pages were uploaded. Our team will create your plan and email
            you within 2-3 business days once it’s ready. You can also select
            your utility and plan below.
          </AlertMessage>
        ),
      )
      .otherwise(() => null)
  )

  const buttonDisabled = match({
    utilityEligibilityStatus,
    planRequired,
    planEligibilityStatus,
    isBusy,
    selectedUtility: viewModel.selectedUtility,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
  })
    .with({ isBusy: true }, () => true)
    .with({ selectedUtility: P.nullish }, () => true)
    // Enrollment: gate on program eligibility.
    .with(
      { utilityEligibilityStatus: EligibilityStatus.INELIGIBLE },
      () => true,
    )
    .with(
      { planRequired: true, planEligibilityStatus: EligibilityStatus.NONE },
      () => true,
    )
    .with(
      {
        planRequired: true,
        planEligibilityStatus: EligibilityStatus.INELIGIBLE,
      },
      () => true,
    )
    .with(
      {
        hasExternalUtilityConnectionRequirement: true,
        hasCompletedExternalUtilityConnectionRequirement: false,
      },
      () => true,
    )
    .otherwise(() => false)

  // Always "Continue": the page header has its own back arrow, and ineligible
  // selections are surfaced via the eligibility notice + a disabled button.
  const ctaButton = match({
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
  })
    .with(
      {
        hasExternalUtilityConnectionRequirement: true,
        hasCompletedExternalUtilityConnectionRequirement: false,
      },
      () => null,
    )
    .otherwise(() => (
      <Button
        id="utility-select-next"
        disabled={buttonDisabled}
        onClick={onNextClick}
      >
        Continue
      </Button>
    ))

  // Profile flow only (no program): when we couldn't read the plan from the bill,
  // let the user skip ahead — the team will create the plan from the bill.
  const showSkip = upload.outcome === 'manual' && !utilityProgram

  const headerText = match({
    hasExternalUtilityConnectionRequirement,
    isConfirming,
  })
    .with(
      { hasExternalUtilityConnectionRequirement: true },
      () => 'Connect your utility account',
    )
    .with({ isConfirming: true }, () => 'Confirm your utility & rate plan')
    .otherwise(() => 'Setup your utility & rate plan')

  const subheaderText = hasExternalUtilityConnectionRequirement
    ? 'Link your utility account so we can confirm your eligibility and unlock rewards.'
    : "Have a bill? Upload it and we'll fill these in. Or pick them yourself."

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingAnimation type="falling" centered />
      </div>
    )
  }

  return (
    <div className="flex justify-between flex-col gap-8 h-full">
      <div className="flex flex-col gap-6">
        <section className="w-full flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Text variant="h3">{headerText}</Text>
            <Text variant="body2">{subheaderText}</Text>
          </div>

          {hasExternalUtilityConnectionRequirement && (
            <AlertMessage variant="security">
              Your information is encrypted, and you can review permissions
              before you grant access.
            </AlertMessage>
          )}

          {!hasExternalUtilityConnectionRequirement && (
            <>
              {billAlreadyOnFile ? (
                <BillOnFileNotice
                  billUrl={upload.persistedBillUrl}
                  onUploadAnother={() => setShowUploaderOverride(true)}
                  detail="You can confirm or change your utility and plan below."
                />
              ) : (
                <>
                  <BillUploadCard
                    fileInputRef={upload.fileInputRef}
                    handleFilesUploaded={upload.handleFilesUploaded}
                    handleUploadClick={upload.handleUploadClick}
                    fileUploadStatus={upload.fileUploadStatus}
                    fileUploadName={upload.fileUploadName}
                    uploadErrorMessage={upload.uploadErrorMessage}
                    uploadSuccessMessage={null}
                    billUrl={upload.billUrl}
                    loadingMessages={BILL_UPLOAD_LOADING_MESSAGES}
                    hideTitle
                    hideSuccessBanner
                  />

                  {isAutoBuilding ? (
                    <div className="flex flex-col items-center gap-2 py-4">
                      <LoadingAnimation type="falling" size="small" />
                      <Text variant="body2" className="text-themed-grey-500">
                        Building your utility plan…
                      </Text>
                    </div>
                  ) : (
                    <>
                      {billResultAlert}

                      {buildError && (
                        <AlertMessage variant="error">
                          {buildError}
                        </AlertMessage>
                      )}
                    </>
                  )}
                </>
              )}

              {!billAlreadyOnFile && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-solid border-t border-themed-grey-200" />
                  <Text variant="body4" className="text-themed-grey-500">
                    or enter manually
                  </Text>
                  <div className="flex-1 border-solid border-t border-themed-grey-200" />
                </div>
              )}

              <div className="flex flex-col gap-2">
                <ComboBox
                  key={viewModel.selectedUtility?.id ?? 'none'}
                  initialValue={initialUtility}
                  name="utility"
                  label="Search Utilities"
                  options={options}
                  setValue={setValue}
                  multiple={false}
                  getValues={getValues}
                  onQueryChange={onQueryChange}
                  register={register}
                  required
                  icon={utilityBoxIcon}
                  optionsLoading={utilitySearchLoading}
                  debounceMs={300}
                  disabled={dropdownsDisabled}
                  {...(utilityProgram
                    ? {}
                    : {
                        // Profile flow only — a custom utility can never be
                        // program-eligible, so don't offer it during enrollment.
                        onAddOption: (query: string) => void addUtility(query),
                        addOptionLabel: (query: string) =>
                          query ? `+ Add "${query}"` : '+ Add my utility',
                      })}
                />
                <EligibilityNotice
                  status={utilityEligibilityStatus}
                  label="Utility provider"
                />
              </div>

              {(planOptions.length > 0 || fetchingPlans) && (
                <>
                  <UtilityPlanDropdown
                    planOptions={planOptions}
                    selectedPlanId={selectedPlanId}
                    onChange={(planId) => setSelectedPlanId(Number(planId))}
                    key="plan-dropdown"
                    label="Select Utility Plan"
                    isLoading={fetchingPlans}
                    required={planRequired}
                    disabled={dropdownsDisabled}
                    onAddPlan={onAddPlan}
                  />
                  <EligibilityNotice
                    status={planEligibilityStatus}
                    label="Plan"
                  />
                </>
              )}

              <PlanRatePreview seasonalRateSummary={seasonalRateSummary} />

              {/* Only when we auto-built a custom plan from the bill — not for a
                  matched catalog plan or a manual pick — offer to redo it by hand. */}
              {onAddPlan &&
                planBuilt &&
                seasonalRateSummary &&
                seasonalRateSummary.length > 0 && (
                  <Text
                    variant="body3"
                    className="text-center text-themed-grey-500"
                  >
                    Did we get it wrong?{' '}
                    <span
                      onClick={onAddPlan}
                      className="!underline cursor-pointer text-themed-blue-500"
                    >
                      Build your own plan.
                    </span>
                  </Text>
                )}
            </>
          )}
        </section>

        {match({
          hasExternalUtilityConnectionRequirement,
          hasCompletedExternalUtilityConnectionRequirement,
          initialSelectedUtility,
          utilityProgram,
        })
          .with(
            {
              hasExternalUtilityConnectionRequirement: true,
              hasCompletedExternalUtilityConnectionRequirement: false,
              utilityProgram: P.nonNullable,
            },
            ({ utilityProgram }) => (
              <section className="flex flex-col gap-4">
                <Text variant="h3">Eligible providers:</Text>
                <div className="shadow-spread p-4 rounded-2xl">
                  <ExternalUtilityConnectionLinks
                    utilityProgram={utilityProgram}
                    utilityRefId={utilityRefId}
                    onButtonClick={onExternalUtilityConnectButtonClick}
                  />
                </div>

                <div className="shadow-spread flex flex-row p-4 rounded-2xl items-center gap-4">
                  <div className="bg-base-grey-100 rounded-xl flex items-center justify-center p-2">
                    <Icon name="HelpCircle" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text variant="body1">Don’t see your provider?</Text>
                    <Text variant="body4">
                      Check back soon, we add new ones often.
                    </Text>
                  </div>
                </div>
              </section>
            ),
          )
          .with(
            {
              hasCompletedExternalUtilityConnectionRequirement: true,
              initialSelectedUtility: P.nonNullable,
            },
            ({ initialSelectedUtility }) => (
              <div className="shadow-spread p-4 rounded-2xl">
                <UtilityConnectLinkRow
                  utility={initialSelectedUtility}
                  url=""
                  isConnected={true}
                />
              </div>
            ),
          )
          .otherwise(() => null)}
      </div>
      <div className="flex flex-col gap-3">
        {showSkip && (
          <Button id="utility-select-skip" variant="secondary" onClick={onSkip}>
            Skip
          </Button>
        )}
        {ctaButton}
      </div>
    </div>
  )
}
